import { createContext, useContext, useEffect, useState } from "react";
import { apiFetch, clearToken, getToken, setToken } from "../api/client.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  async function refresh(expectedToken = getToken()) {
    if (!expectedToken) {
      setSession(null);
      setLoading(false);

      return null;
    }

    try {
      const data = await apiFetch("/auth/me", {
        token: expectedToken,
        showGlobalLoading: false,
      });

      if (getToken() !== expectedToken) {
        return null;
      }

      setSession(data);

      if (data.admin?.teams?.length) {
        const current = localStorage.getItem("metora_team_id");
        const fallback = data.admin.teams.find(team => team.isDefault) || data.admin.teams[0];

        if (!current) {
          localStorage.setItem("metora_team_id", fallback.id);
        }
      }

      return data;
    } catch (error) {
      if (getToken() === expectedToken) {
        clearToken();
        localStorage.removeItem("metora_team_id");
        setSession(null);
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh().catch(() => undefined);
  }, []);

  async function loginWithGoogleCredential(googleToken) {
    const data = await apiFetch("/auth/google", {
      method: "POST",
      body: googleToken,
      showGlobalLoading: false,
    });
    setToken(data.token);
    localStorage.setItem("metora_team_id", data.team.id);
    await refresh(data.token);
  }

  async function createAdminAccount({ name, email, password }) {
    const data = await apiFetch("/auth/admin/create", {
      method: "POST",
      body: { name, email, password },
      showGlobalLoading: false,
    });
    setToken(data.token);
    localStorage.setItem("metora_team_id", data.team.id);
    await refresh(data.token);
  }

  async function signInAdminWithPassword({ email, password }) {
    const data = await apiFetch("/auth/admin/sign-in", {
      method: "POST",
      body: { email, password },
      showGlobalLoading: false,
    });
    setToken(data.token);
    localStorage.setItem("metora_team_id", data.team.id);
    await refresh(data.token);
  }

  async function joinTeam(inviteCode) {
    const data = await apiFetch("/auth/join", {
      method: "POST",
      body: { inviteCode },
      showGlobalLoading: false,
    });
    setToken(data.token);
    localStorage.setItem("metora_team_id", data.team.id);
    await refresh(data.token);
  }

  function logout() {
    clearToken();
    localStorage.removeItem("metora_team_id");
    setSession(null);
  }

  const value = {
    session,
    loading,
    loginWithGoogleCredential,
    createAdminAccount,
    signInAdminWithPassword,
    joinTeam,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
